"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import styled from "styled-components"
import { Play, Film, Calendar, User, Image, Loader, Search, Filter, Heart, MessageSquare, Download, X, Volume2, VolumeX, Pause, MoreVertical, Grid, Bookmark } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { useTheme } from "../context/ThemeContext"

// ===================== 공통 스타일 =====================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 768px) {
    gap: 16px;
  }

  @media (max-width: 420px) {
    gap: 12px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 420px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`

const Title = styled.h1`
  font-size: 24px;
  margin: 0;
  color: ${props => props.theme.textPrimary};

  @media (max-width: 768px) {
    font-size: 20px;
  }

  @media (max-width: 420px) {
    font-size: 18px;
  }
`

const ViewToggle = styled.div`
  display: flex;
  gap: 4px;
  background: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 4px;
  box-shadow: ${props => props.theme.shadow};
`

const ViewToggleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '400'};
  background: ${props => props.$active ? props.theme.buttonPrimary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.textSecondary};
  transition: all 0.2s;

  &:hover {
    background: ${props => props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground};
  }
`

const Tabs = styled.div`
  display: flex;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  overflow-x: auto;

  @media (max-width: 768px) {
    gap: 0;

    &::-webkit-scrollbar {
      height: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background: ${props => props.theme.border};
      border-radius: 2px;
    }
  }
`

const Tab = styled.button`
  padding: 12px 24px;
  background-color: ${(props) => (props.$active ? props.theme.buttonPrimary : "transparent")};
  color: ${(props) => (props.$active ? "white" : props.theme.textPrimary)};
  border: none;
  cursor: pointer;
  font-weight: ${(props) => (props.$active ? "600" : "400")};
  white-space: nowrap;
  min-width: fit-content;

  &:hover {
    background-color: ${(props) => (props.$active ? props.theme.buttonPrimaryHover : props.theme.hoverBackground)};
  }

  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 14px;
  }

  @media (max-width: 420px) {
    padding: 8px 12px;
    font-size: 13px;
  }
`

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;
  }

  @media (max-width: 420px) {
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }
`

const MediaCard = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: ${props => props.theme.shadow};
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-4px);
  }
`

const MediaThumbnail = styled.div`
  position: relative;
  padding-top: 56.25%; /* 16:9 aspect ratio */
  background-color: ${props => props.theme.border};

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .play-button {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 48px;
    height: 48px;
    background-color: ${props => `rgba(${parseInt(props.theme.buttonPrimary.slice(1, 3), 16)}, ${parseInt(props.theme.buttonPrimary.slice(3, 5), 16)}, ${parseInt(props.theme.buttonPrimary.slice(5, 7), 16)}, 0.8)`};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s;
  }

  &:hover .play-button {
    opacity: 1;
  }

  .tag {
    position: absolute;
    top: 8px;
    left: 8px;
    background-color: ${props => `rgba(${parseInt(props.theme.buttonPrimary.slice(1, 3), 16)}, ${parseInt(props.theme.buttonPrimary.slice(3, 5), 16)}, ${parseInt(props.theme.buttonPrimary.slice(5, 7), 16)}, 0.8)`};
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`

const MediaInfo = styled.div`
  padding: 12px;

  @media (max-width: 420px) {
    padding: 10px;
  }
`

const MediaTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
  min-height: 2.6em;
  color: ${props => props.theme.textPrimary};

  @media (max-width: 768px) {
    font-size: 15px;
  }

  @media (max-width: 420px) {
    font-size: 14px;
    -webkit-line-clamp: 3;
    min-height: 3.9em;
    margin: 0 0 6px 0;
  }
`

const MediaMeta = styled.div`
  display: flex;
  align-items: center;
  color: ${props => props.theme.textSecondary};
  font-size: 12px;
  gap: 12px;
  flex-wrap: wrap;

  .meta-item {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  @media (max-width: 768px) {
    gap: 10px;
  }

  @media (max-width: 420px) {
    gap: 8px;
    font-size: 11px;

    .meta-item {
      gap: 3px;
    }
  }
`

const LoadingIndicator = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.textSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${props => props.theme.error};
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.error};
  border-radius: 8px;
`

// 검색 및 필터 스타일
const SearchSection = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }

  @media (max-width: 420px) {
    margin-bottom: 16px;
  }
`

const SearchBar = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: ${props => props.theme.shadow};

  input {
    border: none;
    flex: 1;
    margin-left: 8px;
    outline: none;
    font-size: 14px;
    background: transparent;
    color: ${props => props.theme.textPrimary};

    &::placeholder {
      color: ${props => props.theme.textMuted};
    }
  }
`

const FilterDropdown = styled.div`
  position: relative;
  display: inline-block;
`

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  transition: all 0.2s;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
    border-color: ${props => props.theme.border};
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const FilterMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  padding: 8px;
  min-width: 200px;
  z-index: 100;
  margin-top: 4px;
`

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }

  input {
    margin: 0;
  }
`

// 비디오 플레이어 모달 스타일
const VideoModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 12px;
  }

  @media (max-width: 420px) {
    padding: 8px;
  }
`

const VideoModalContainer = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background-color: ${props => props.theme.cardBackground};
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    max-width: 95vw;
    max-height: 95vh;
    border-radius: 8px;
  }

  @media (max-width: 420px) {
    max-width: 98vw;
    max-height: 98vh;
    border-radius: 6px;
  }
`

const VideoPlayerContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 800px;
  aspect-ratio: 16/9;

  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.7));
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s;
`

const ControlButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`

const ProgressBar = styled.div`
  flex: 1;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
  position: relative;

  .progress {
    height: 100%;
    background-color: ${props => props.theme.buttonPrimary};
    border-radius: 2px;
    transition: width 0.1s;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`

const VideoInfo = styled.div`
  padding: 20px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 420px) {
    padding: 12px;
  }
`

const VideoTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 420px) {
    font-size: 14px;
    margin: 0 0 8px 0;
  }
`

const VideoMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
  font-size: 14px;
  color: ${props => props.theme.textSecondary};
  flex-wrap: wrap;

  .author {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;

    &:hover {
      color: ${props => props.theme.buttonPrimary};
    }
  }

  @media (max-width: 768px) {
    gap: 12px;
    font-size: 13px;
  }

  @media (max-width: 420px) {
    gap: 8px;
    font-size: 12px;
    margin-bottom: 8px;
  }
`

const VideoActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 420px) {
    gap: 8px;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  background-color: ${props => props.theme.cardBackground};
  color: ${props => props.theme.textPrimary};
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.theme.hoverBackground};
  }

  &.liked {
    background-color: ${props => props.theme.cardBackground};
    color: ${props => props.theme.error};
    border-color: ${props => props.theme.error};
  }

  @media (max-width: 420px) {
    padding: 6px 10px;
    font-size: 11px;
    gap: 4px;
  }
`

// ===================== 숏폼(릴스) 전용 스타일 =====================

const ShortsContainer = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 64px);
  background: #000;
  border-radius: 12px;
  overflow: hidden;
  scroll-snap-type: y mandatory;
  overflow-y: scroll;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  @media (max-width: 768px) {
    height: calc(100vh - 56px);
    border-radius: 8px;
  }
`

const ShortsItem = styled.div`
  position: relative;
  width: 100%;
  height: calc(100vh - 64px);
  scroll-snap-align: start;
  scroll-snap-stop: always;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;

  @media (max-width: 768px) {
    height: calc(100vh - 56px);
  }
`

const ShortsVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
  cursor: pointer;
`

const ShortsOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`

const ShortsTopGradient = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.5), transparent);
  display: flex;
  align-items: flex-start;
  padding: 16px 20px;
  pointer-events: auto;
`

const ShortsBottomGradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 250px;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  pointer-events: none;
`

const ShortsAuthorInfo = styled.div`
  position: absolute;
  bottom: 20px;
  left: 16px;
  right: 72px;
  pointer-events: auto;
  z-index: 2;

  @media (max-width: 420px) {
    bottom: 16px;
    left: 12px;
    right: 64px;
  }
`

const ShortsAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`

const ShortsAvatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  background: #333;

  @media (max-width: 420px) {
    width: 32px;
    height: 32px;
  }
`

const ShortsAuthorName = styled.div`
  color: white;

  .name {
    font-size: 14px;
    font-weight: 700;
  }

  .username {
    font-size: 12px;
    color: rgba(255,255,255,0.7);
  }
`

const ShortsContent = styled.div`
  color: rgba(255,255,255,0.9);
  font-size: 13px;
  line-height: 18px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 4px;
`

const ShortsTags = styled.div`
  color: #FFD700;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ShortsActions = styled.div`
  position: absolute;
  bottom: 90px;
  right: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  pointer-events: auto;
  z-index: 2;

  @media (max-width: 420px) {
    bottom: 80px;
    right: 8px;
    gap: 16px;
  }
`

const ShortsActionBtn = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: white;
  padding: 0;

  .icon {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .label {
    font-size: 11px;
    font-weight: 500;
  }

  &:active {
    transform: scale(0.9);
  }
`

const ShortsPlayPauseOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  pointer-events: none;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.3s;
`

const ShortsLoadingOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  pointer-events: none;
`

const ShortsHeader = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 700;
`

const ShortsProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255,255,255,0.2);
  z-index: 3;

  .bar {
    height: 100%;
    background: #FFD700;
    transition: width 0.2s;
  }
`

// ===================== 숏폼 개별 비디오 컴포넌트 =====================

function ShortsVideoItem({ post, isActive, isMuted, onToggleMute, onLike, onBookmark, onAuthorClick, onVideoEnd, isRepeatMode }) {
  const videoRef = useRef(null);
  const [isPaused, setIsPaused] = useState(false);
  const [showPlayIcon, setShowPlayIcon] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [isVideoLoading, setIsVideoLoading] = useState(true);
  const playIconTimeout = useRef(null);

  // 비디오 URL 결정
  const videoUrl = post.images?.find(img => {
    const url = (img.url || img.image_url || '').toLowerCase();
    return url.includes('.mp4') || url.includes('.mov') || url.includes('.webm');
  });
  const fullVideoUrl = videoUrl ? (videoUrl.url || videoUrl.image_url) : null;
  const resolvedUrl = fullVideoUrl
    ? (fullVideoUrl.startsWith('http') ? fullVideoUrl : `https://story.antsnest.co.kr${fullVideoUrl.startsWith('/') ? '' : '/'}${fullVideoUrl}`)
    : null;

  // 아바타 URL
  const avatarUrl = post.author_avatar
    ? (post.author_avatar.startsWith('http') ? post.author_avatar : `https://story.antsnest.co.kr${post.author_avatar}`)
    : 'https://file.skynet.re.kr/antsnest/ProfileImage/S_PHOTO20240214083913AntsNestLogo.png';

  // 활성/비활성 시 재생 제어
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isActive && !isPaused) {
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [isActive, isPaused]);

  // 음소거 동기화
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // 탭하여 재생/일시정지
  const handleVideoClick = () => {
    setIsPaused(prev => !prev);
    setShowPlayIcon(true);
    if (playIconTimeout.current) clearTimeout(playIconTimeout.current);
    playIconTimeout.current = setTimeout(() => setShowPlayIcon(false), 800);
  };

  // 영상 진행률 업데이트
  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (video && video.duration) {
      setVideoProgress((video.currentTime / video.duration) * 100);
    }
  };

  // 영상 종료 시
  const handleEnded = () => {
    if (isRepeatMode) {
      const video = videoRef.current;
      if (video) {
        video.currentTime = 0;
        video.play().catch(() => {});
      }
    } else {
      onVideoEnd?.();
    }
  };

  // cleanup
  useEffect(() => {
    return () => {
      if (playIconTimeout.current) clearTimeout(playIconTimeout.current);
    };
  }, []);

  const formatCount = (count) => {
    if (!count) return '0';
    if (count >= 10000) return `${(count / 10000).toFixed(1)}만`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return count.toString();
  };

  if (!resolvedUrl) return null;

  return (
    <ShortsItem>
      <ShortsVideo
        ref={videoRef}
        src={resolvedUrl}
        playsInline
        loop={isRepeatMode}
        muted={isMuted}
        preload="auto"
        onClick={handleVideoClick}
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onWaiting={() => setIsVideoLoading(true)}
        onCanPlay={() => setIsVideoLoading(false)}
        onLoadedData={() => setIsVideoLoading(false)}
      />

      <ShortsOverlay>
        {/* 상단 그라데이션 + 헤더 */}
        <ShortsTopGradient>
          <ShortsHeader>숏폼</ShortsHeader>
        </ShortsTopGradient>

        {/* 하단 그라데이션 */}
        <ShortsBottomGradient />
      </ShortsOverlay>

      {/* 로딩 */}
      {isVideoLoading && isActive && (
        <ShortsLoadingOverlay>
          <Loader size={40} className="animate-spin" />
        </ShortsLoadingOverlay>
      )}

      {/* 재생/일시정지 아이콘 */}
      <ShortsPlayPauseOverlay $visible={showPlayIcon}>
        {isPaused ? <Play size={32} fill="white" /> : <Pause size={32} />}
      </ShortsPlayPauseOverlay>

      {/* 좌측 하단: 작성자 정보 */}
      <ShortsAuthorInfo>
        <ShortsAuthorRow onClick={() => onAuthorClick?.(post)}>
          <ShortsAvatar
            src={avatarUrl}
            alt={post.author_username}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://file.skynet.re.kr/antsnest/ProfileImage/S_PHOTO20240214083913AntsNestLogo.png';
            }}
          />
          <ShortsAuthorName>
            <div className="name">{post.author_full_name || post.author_username || 'Unknown'}</div>
            {post.author_username && <div className="username">@{post.author_username}</div>}
          </ShortsAuthorName>
        </ShortsAuthorRow>

        {post.content && (
          <ShortsContent>{post.content}</ShortsContent>
        )}

        {post.tags && post.tags.length > 0 && (
          <ShortsTags>
            {post.tags.map(tag => `#${tag}`).join(' ')}
          </ShortsTags>
        )}
      </ShortsAuthorInfo>

      {/* 우측: 액션 버튼 */}
      <ShortsActions>
        {/* 좋아요 */}
        <ShortsActionBtn onClick={() => onLike?.(post.id)}>
          <div className="icon">
            <Heart size={26} fill={post.is_liked ? '#ff4444' : 'none'} color={post.is_liked ? '#ff4444' : 'white'} />
          </div>
          <span className="label">{formatCount(post.like_count)}</span>
        </ShortsActionBtn>

        {/* 댓글 */}
        <ShortsActionBtn onClick={() => window.open(`/post/${post.id}`, '_blank')}>
          <div className="icon">
            <MessageSquare size={26} color="white" />
          </div>
          <span className="label">{formatCount(post.comment_count)}</span>
        </ShortsActionBtn>

        {/* 북마크 */}
        <ShortsActionBtn onClick={() => onBookmark?.(post.id)}>
          <div className="icon">
            <Bookmark size={26} fill={post.is_bookmarked ? '#FFD700' : 'none'} color={post.is_bookmarked ? '#FFD700' : 'white'} />
          </div>
          <span className="label">저장</span>
        </ShortsActionBtn>

        {/* 음소거 */}
        <ShortsActionBtn onClick={onToggleMute}>
          <div className="icon">
            {isMuted ? <VolumeX size={26} color="white" /> : <Volume2 size={26} color="white" />}
          </div>
          <span className="label">{isMuted ? '음소거' : '소리'}</span>
        </ShortsActionBtn>
      </ShortsActions>

      {/* 진행 바 */}
      <ShortsProgressBar>
        <div className="bar" style={{ width: `${videoProgress}%` }} />
      </ShortsProgressBar>
    </ShortsItem>
  );
}

// ===================== 메인 컴포넌트 =====================

export default function Media() {
  const { isLoggedIn, user } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('shorts'); // 'grid' | 'shorts'
  const [activeTab, setActiveTab] = useState("all");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [progress, setProgress] = useState(0);
  const [filters, setFilters] = useState({
    sortBy: 'newest',
    duration: 'all',
    author: 'all'
  });
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  // 숏폼 전용 state
  const [shortsPosts, setShortsPosts] = useState([]);
  const [shortsPage, setShortsPage] = useState(1);
  const [shortsHasMore, setShortsHasMore] = useState(true);
  const [shortsLoading, setShortsLoading] = useState(true);
  const [shortsMuted, setShortsMuted] = useState(false);
  const [isRepeatMode] = useState(false);
  const [activeShortIndex, setActiveShortIndex] = useState(0);
  const shortsContainerRef = useRef(null);

  // 비디오 URL 판별
  const isVideo = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.webm') || lower.includes('.avi');
  };

  // 숏폼 영상 전용 API 사용 (/post/videos)
  const fetchShortsPosts = useCallback(async (pageNum = 1, append = false) => {
    try {
      if (!append) setShortsLoading(true);
      const response = await fetch(`/post/videos?page=${pageNum}&limit=20`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success && data.posts) {
        // images가 JSON string인 경우 파싱
        const parsed = data.posts.map(p => ({
          ...p,
          images: typeof p.images === 'string' ? JSON.parse(p.images) : p.images,
          hashtags: p.tags || p.hashtags || []
        }));

        if (append) {
          setShortsPosts(prev => [...prev, ...parsed]);
        } else {
          setShortsPosts(parsed);
        }
        setShortsHasMore(data.has_more);
        setShortsPage(pageNum);
      }
    } catch (err) {
      console.error('숏폼 로드 오류:', err);
    } finally {
      setShortsLoading(false);
    }
  }, []);

  // 포스트 목록 가져오기 (그리드용)
  const fetchPosts = async (startPage = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      let allVideos = append ? [] : [];
      let currentPage = startPage;
      let serverHasMore = true;
      const MIN_VIDEOS = 12;
      const MAX_PAGES = 10;

      while (allVideos.length < MIN_VIDEOS && serverHasMore && currentPage < startPage + MAX_PAGES) {
        const response = await fetch(`/post/list?page=${currentPage}&limit=50`, {
          credentials: 'include'
        });
        const data = await response.json();

        if (!data.success || !data.posts || data.posts.length === 0) {
          serverHasMore = false;
          break;
        }

        const videoPosts = data.posts.filter(post =>
          post.images && post.images.length > 0 &&
          post.images.some(img => isVideo(img.url || img.image_url || ''))
        );
        allVideos.push(...videoPosts);

        serverHasMore = data.posts.length >= 50;
        currentPage++;
      }

      if (append) {
        setPosts(prev => [...prev, ...allVideos]);
      } else {
        setPosts(allVideos);
      }

      setPage(currentPage);
      setHasMore(serverHasMore);
    } catch (error) {
      setError("서버 연결에 문제가 발생했습니다.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // 뷰 모드에 따라 데이터 로드
  useEffect(() => {
    if (viewMode === 'shorts') {
      fetchShortsPosts(1);
    } else {
      fetchPosts(1);
    }
  }, [viewMode, fetchShortsPosts]);

  // 숏폼: IntersectionObserver로 활성 영상 감지
  useEffect(() => {
    if (viewMode !== 'shorts') return;

    const container = shortsContainerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            if (!isNaN(index)) {
              setActiveShortIndex(index);
            }
          }
        });
      },
      {
        root: container,
        threshold: 0.6
      }
    );

    const items = container.querySelectorAll('[data-index]');
    items.forEach(item => observer.observe(item));

    return () => observer.disconnect();
  }, [viewMode, shortsPosts]);

  // 숏폼: 스크롤 끝 감지 → 더 로드
  useEffect(() => {
    if (viewMode !== 'shorts') return;

    const container = shortsContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < clientHeight && shortsHasMore && !shortsLoading) {
        fetchShortsPosts(shortsPage + 1, true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [viewMode, shortsPage, shortsHasMore, shortsLoading, fetchShortsPosts]);

  // 숏폼: 다음 영상으로 자동 이동
  const goToNextShort = useCallback(() => {
    const container = shortsContainerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-index]');
    const nextIndex = activeShortIndex + 1;
    if (nextIndex < items.length) {
      items[nextIndex].scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeShortIndex]);

  // 좋아요 토글
  const handleLike = async (postId) => {
    if (!isLoggedIn) return;
    try {
      const response = await fetch(`/like/toggle/${postId}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        // 숏폼 posts 업데이트
        setShortsPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, is_liked: data.is_liked ?? !p.is_liked, like_count: data.like_count ?? (p.is_liked ? p.like_count - 1 : p.like_count + 1) }
            : p
        ));
      }
    } catch (err) {
      console.error('좋아요 오류:', err);
    }
  };

  // 북마크 토글
  const handleBookmark = async (postId) => {
    if (!isLoggedIn) return;
    try {
      const response = await fetch(`/bookmark/toggle/${postId}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setShortsPosts(prev => prev.map(p =>
          p.id === postId
            ? { ...p, is_bookmarked: data.is_bookmarked ?? !p.is_bookmarked }
            : p
        ));
      }
    } catch (err) {
      console.error('북마크 오류:', err);
    }
  };

  // 작성자 클릭
  const handleAuthorClick = (post) => {
    navigate(`/profile/${post.author_username}`);
  };

  // 더보기 핸들러 (그리드)
  const loadMore = () => {
    fetchPosts(page, true);
  };

  // 미디어 파일 확장자 체크
  const isVideoUrl = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.includes('.mp4') || lower.includes('.mov') || lower.includes('.avi') || lower.includes('.webm') || lower.includes('.mkv');
  };

  // 태그 + URL 기반으로 미디어 타입 결정
  const getMediaType = (tags, images) => {
    const tagList = tags || [];
    if (tagList.includes("동영상") || tagList.includes("video") || tagList.includes("AI영상") || tagList.includes("GrokVideo")) return "video";
    if (tagList.includes("튜토리얼") || tagList.includes("tutorial")) return "tutorial";
    if (tagList.includes("브이로그") || tagList.includes("vlog")) return "vlog";
    if (tagList.includes("이벤트") || tagList.includes("event")) return "event";
    if (images && images.some(img => isVideoUrl(img.url))) return "video";
    return "기타";
  };

  // 게시물에 미디어 타입 추가
  const mediaItems = posts.map(post => {
    const tags = post.hashtags || post.tags || [];
    const mediaType = getMediaType(tags, post.images);
    const hasVideo = post.images && post.images.some(img => isVideoUrl(img.url));

    return {
      ...post,
      mediaType,
      hasVideo,
      title: post.content ? (post.content.length > 50 ? post.content.substring(0, 50) + '...' : post.content) : "제목 없음",
      thumbnail: post.images && post.images.length > 0 ? post.images[0].url : null,
      videoUrl: hasVideo ? post.images.find(img => isVideoUrl(img.url))?.url : null
    };
  });

  // 필터링 로직
  const getFilteredMedia = () => {
    let filtered = mediaItems;
    if (activeTab !== "all") {
      filtered = filtered.filter(item => item.mediaType === activeTab);
    }
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.author_username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.hashtags && item.hashtags.some(tag =>
          tag.toLowerCase().includes(searchQuery.toLowerCase())
        ))
      );
    }
    if (filters.author !== 'all') {
      if (filters.author === 'me') {
        filtered = filtered.filter(item => item.author_id === user?.id);
      }
    }
    switch (filters.sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        break;
      case 'popular':
        filtered.sort((a, b) => (b.like_count || 0) - (a.like_count || 0));
        break;
      default:
        break;
    }
    return filtered;
  };

  const filteredMedia = getFilteredMedia();

  // 비디오 플레이어 핸들러 (그리드 모달)
  const openVideoModal = (item) => {
    setSelectedVideo(item);
    setIsPlaying(false);
    setProgress(0);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
    setIsPlaying(false);
    setProgress(0);
  };

  const togglePlay = () => {
    const video = document.querySelector('.video-player');
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    const video = document.querySelector('.video-player');
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleProgress = (e) => {
    const video = document.querySelector('.video-player');
    if (video) {
      const rect = e.currentTarget.getBoundingClientRect();
      const pos = (e.clientX - rect.left) / rect.width;
      video.currentTime = pos * video.duration;
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  // ===================== 숏폼 뷰 렌더링 =====================
  if (viewMode === 'shorts') {
    return (
      <Container>
        <Header>
          <Title theme={theme}>미디어</Title>
          <ViewToggle theme={theme}>
            <ViewToggleButton theme={theme} $active={viewMode === 'shorts'} onClick={() => setViewMode('shorts')}>
              <Play size={16} />
              숏폼
            </ViewToggleButton>
            <ViewToggleButton theme={theme} $active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
              <Grid size={16} />
              그리드
            </ViewToggleButton>
          </ViewToggle>
        </Header>

        {shortsLoading && shortsPosts.length === 0 ? (
          <LoadingIndicator theme={theme}>
            <Loader size={32} className="animate-spin" />
            <div>영상을 불러오는 중...</div>
          </LoadingIndicator>
        ) : shortsPosts.length === 0 ? (
          <LoadingIndicator theme={theme}>
            <Film size={32} />
            <div>영상이 없습니다</div>
          </LoadingIndicator>
        ) : (
          <ShortsContainer ref={shortsContainerRef}>
            {shortsPosts.map((post, index) => (
              <div key={post.id} data-index={index}>
                <ShortsVideoItem
                  post={post}
                  isActive={index === activeShortIndex}
                  isMuted={shortsMuted}
                  onToggleMute={() => setShortsMuted(prev => !prev)}
                  onLike={handleLike}
                  onBookmark={handleBookmark}
                  onAuthorClick={handleAuthorClick}
                  onVideoEnd={goToNextShort}
                  isRepeatMode={isRepeatMode}
                />
              </div>
            ))}
          </ShortsContainer>
        )}
      </Container>
    );
  }

  // ===================== 그리드 뷰 렌더링 (기존) =====================
  return (
    <Container>
      <Header>
        <Title theme={theme}>미디어</Title>
        <ViewToggle theme={theme}>
          <ViewToggleButton theme={theme} $active={viewMode === 'shorts'} onClick={() => setViewMode('shorts')}>
            <Play size={16} />
            숏폼
          </ViewToggleButton>
          <ViewToggleButton theme={theme} $active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
            <Grid size={16} />
            그리드
          </ViewToggleButton>
        </ViewToggle>
      </Header>

      {/* 검색 및 필터 섹션 */}
      <SearchSection>
        <SearchBar theme={theme}>
          <Search size={20} color={theme.textMuted} />
          <input
            type="text"
            placeholder="미디어 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>

        <FilterDropdown>
          <FilterButton theme={theme} onClick={() => setShowFilters(!showFilters)}>
            <Filter size={16} />
            필터
          </FilterButton>
          {showFilters && (
            <FilterMenu theme={theme}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                정렬 기준
              </div>
              <FilterOption theme={theme}>
                <input
                  type="radio"
                  name="sortBy"
                  value="newest"
                  checked={filters.sortBy === 'newest'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                최신순
              </FilterOption>
              <FilterOption theme={theme}>
                <input
                  type="radio"
                  name="sortBy"
                  value="oldest"
                  checked={filters.sortBy === 'oldest'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                오래된 순
              </FilterOption>
              <FilterOption theme={theme}>
                <input
                  type="radio"
                  name="sortBy"
                  value="popular"
                  checked={filters.sortBy === 'popular'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                />
                인기순
              </FilterOption>

              {isLoggedIn && (
                <>
                  <hr style={{ margin: '8px 0', border: 'none', borderTop: `1px solid ${theme.border}` }} />
                  <div style={{ fontSize: '12px', fontWeight: '600', color: theme.textSecondary, marginBottom: '8px', textTransform: 'uppercase' }}>
                    작성자
                  </div>
                  <FilterOption theme={theme}>
                    <input
                      type="radio"
                      name="author"
                      value="all"
                      checked={filters.author === 'all'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    모든 사용자
                  </FilterOption>
                  <FilterOption theme={theme}>
                    <input
                      type="radio"
                      name="author"
                      value="me"
                      checked={filters.author === 'me'}
                      onChange={(e) => handleFilterChange('author', e.target.value)}
                    />
                    내 미디어만
                  </FilterOption>
                </>
              )}
            </FilterMenu>
          )}
        </FilterDropdown>
      </SearchSection>

      <Tabs theme={theme}>
        <Tab theme={theme} $active={activeTab === "all"} onClick={() => setActiveTab("all")}>
          전체
        </Tab>
        <Tab theme={theme} $active={activeTab === "video"} onClick={() => setActiveTab("video")}>
          비디오
        </Tab>
        <Tab theme={theme} $active={activeTab === "tutorial"} onClick={() => setActiveTab("tutorial")}>
          튜토리얼
        </Tab>
        <Tab theme={theme} $active={activeTab === "vlog"} onClick={() => setActiveTab("vlog")}>
          브이로그
        </Tab>
        <Tab theme={theme} $active={activeTab === "event"} onClick={() => setActiveTab("event")}>
          이벤트
        </Tab>
      </Tabs>

      {loading ? (
        <LoadingIndicator theme={theme}>
          <Loader size={32} className="animate-spin" />
          <div>미디어를 불러오는 중...</div>
        </LoadingIndicator>
      ) : error ? (
        <ErrorMessage theme={theme}>{error}</ErrorMessage>
      ) : filteredMedia.length === 0 ? (
        <LoadingIndicator theme={theme}>
          <Image size={32} />
          <div>표시할 미디어가 없습니다</div>
        </LoadingIndicator>
      ) : (
        <MediaGrid>
          {filteredMedia.map((item) => (
            <MediaCard
              key={item.id}
              theme={theme}
              onClick={() => {
                if (item.hasVideo) {
                  openVideoModal(item);
                } else {
                  window.open(item.thumbnail, '_blank');
                }
              }}
            >
              <MediaThumbnail theme={theme}>
                {item.hasVideo && item.videoUrl ? (
                  <>
                    <div className="video-spinner" style={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                      width: '32px', height: '32px', border: `3px solid ${theme.border}`,
                      borderTop: `3px solid ${theme.buttonPrimary}`, borderRadius: '50%',
                      animation: 'spin 1s linear infinite', zIndex: 1
                    }} />
                    <style>{`@keyframes spin { 0% { transform: translate(-50%, -50%) rotate(0deg); } 100% { transform: translate(-50%, -50%) rotate(360deg); } }`}</style>
                    <video
                      src={item.videoUrl}
                      muted
                      preload="metadata"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 2 }}
                      onLoadedData={(e) => {
                        e.target.currentTime = 0.5;
                        const spinner = e.target.previousElementSibling?.previousElementSibling;
                        if (spinner) spinner.style.display = 'none';
                      }}
                    />
                  </>
                ) : (
                  <img
                    src={item.thumbnail || "/placeholder.svg"}
                    alt={item.title}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder.svg?height=200&width=300";
                    }}
                  />
                )}
                <div className="play-button">
                  {item.hasVideo ? <Play size={24} fill="white" /> : <Image size={24} />}
                </div>
                {item.mediaType && item.mediaType !== "기타" && (
                  <div className="tag">{item.mediaType}</div>
                )}
                {item.images && item.images.length > 1 && (
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '11px'
                  }}>
                    +{item.images.length - 1}
                  </div>
                )}
              </MediaThumbnail>
              <MediaInfo>
                <MediaTitle theme={theme}>{item.title}</MediaTitle>
                <MediaMeta theme={theme}>
                  <div className="meta-item">
                    <Calendar size={12} />
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                  <div
                    className="meta-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/profile/${item.author_username}`);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <User size={12} />
                    <span>{item.author_username}</span>
                  </div>
                  {(item.like_count > 0 || item.comment_count > 0) && (
                    <>
                      {item.like_count > 0 && (
                        <div className="meta-item">
                          <Heart size={12} />
                          <span>{item.like_count}</span>
                        </div>
                      )}
                      {item.comment_count > 0 && (
                        <div className="meta-item">
                          <MessageSquare size={12} />
                          <span>{item.comment_count}</span>
                        </div>
                      )}
                    </>
                  )}
                </MediaMeta>
              </MediaInfo>
            </MediaCard>
          ))}
        </MediaGrid>
      )}

      {/* 더보기 버튼 */}
      {!loading && hasMore && filteredMedia.length > 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <button
            onClick={loadMore}
            disabled={loadingMore}
            style={{
              padding: '12px 32px',
              backgroundColor: theme.buttonPrimary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: loadingMore ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '600',
              opacity: loadingMore ? 0.7 : 1,
            }}
          >
            {loadingMore ? '불러오는 중...' : '더보기'}
          </button>
        </div>
      )}

      {/* 비디오 모달 */}
      {selectedVideo && (
        <VideoModalOverlay onClick={closeVideoModal}>
          <VideoModalContainer theme={theme} onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={closeVideoModal}>
              <X size={20} />
            </CloseButton>

            <VideoPlayerContainer
              onMouseEnter={() => setShowControls(true)}
              onMouseLeave={() => setShowControls(false)}
            >
              {selectedVideo.videoUrl ? (
                <video
                  className="video-player"
                  src={selectedVideo.videoUrl}
                  poster={selectedVideo.thumbnail}
                  onTimeUpdate={(e) => {
                    const video = e.target;
                    setProgress((video.currentTime / video.duration) * 100);
                  }}
                  onEnded={() => setIsPlaying(false)}
                  onError={() => console.log('Video load error')}
                />
              ) : (
                <img
                  src={selectedVideo.thumbnail}
                  alt={selectedVideo.title}
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}

              {selectedVideo.videoUrl && (
                <VideoControls $visible={showControls}>
                  <ControlButton onClick={togglePlay}>
                    {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                  </ControlButton>

                  <ControlButton onClick={toggleMute}>
                    {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </ControlButton>

                  <ProgressBar theme={theme} onClick={handleProgress}>
                    <div className="progress" style={{ width: `${progress}%` }} />
                  </ProgressBar>

                  <ControlButton
                    onClick={() => {
                      const video = document.querySelector('.video-player');
                      if (video.requestFullscreen) {
                        video.requestFullscreen();
                      }
                    }}
                  >
                    <MoreVertical size={20} />
                  </ControlButton>
                </VideoControls>
              )}
            </VideoPlayerContainer>

            <VideoInfo theme={theme}>
              <VideoTitle>{selectedVideo.title}</VideoTitle>
              <VideoMeta theme={theme}>
                <div
                  className="author"
                  onClick={() => {
                    closeVideoModal();
                    navigate(`/profile/${selectedVideo.author_username}`);
                  }}
                >
                  <User size={16} />
                  {selectedVideo.author_username}
                </div>
                <div>
                  <Calendar size={16} />
                  {formatDate(selectedVideo.created_at)}
                </div>
                <div>
                  <Heart size={16} />
                  {selectedVideo.like_count || 0} 좋아요
                </div>
                <div>
                  <MessageSquare size={16} />
                  {selectedVideo.comment_count || 0} 댓글
                </div>
              </VideoMeta>

              {selectedVideo.content && selectedVideo.content !== selectedVideo.title && (
                <div style={{
                  marginTop: '12px',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: theme.textPrimary
                }}>
                  {selectedVideo.content}
                </div>
              )}

              {selectedVideo.hashtags && selectedVideo.hashtags.length > 0 && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '6px',
                  marginTop: '12px'
                }}>
                  {selectedVideo.hashtags.map((tag, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: theme.borderLight,
                        borderRadius: '12px',
                        fontSize: '12px',
                        color: theme.textSecondary
                      }}
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}

              <VideoActions>
                <ActionButton theme={theme}>
                  <Heart size={16} />
                  좋아요
                </ActionButton>
                <ActionButton theme={theme}>
                  <MessageSquare size={16} />
                  댓글
                </ActionButton>
                {selectedVideo.videoUrl && (
                  <ActionButton theme={theme}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = selectedVideo.videoUrl;
                      link.download = `video_${selectedVideo.id}.mp4`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download size={16} />
                    다운로드
                  </ActionButton>
                )}
              </VideoActions>
            </VideoInfo>
          </VideoModalContainer>
        </VideoModalOverlay>
      )}
    </Container>
  );
}
